import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Form,
  Input,
  Button,
  Spin,
  message,
  Typography,
  Space,
  Row,
  Col,
  DatePicker,
  Table,
  Empty,
} from 'antd';
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  HomeOutlined,
  CarOutlined,
  LockOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import PublicLayout from '../components/landing/PublicLayout';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';

const { Title, Text, Paragraph } = Typography;

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

interface CustomerData {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  birthday?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
}

interface Vehicle {
  id: number;
  year: number;
  make: string;
  model: string;
  vin: string;
  license_plate: string;
  mileage: number;
}

interface ProfileFormValues {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  birthday?: Dayjs;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
}

const CustomerProfile: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, customerData, accessToken } = useCustomerAuth();
  const [form] = Form.useForm<ProfileFormValues>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm] = Form.useForm();
  const loadProfileData = React.useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };

      // Get customer vehicles
      const vehiclesRes = await axios.get(
        `${API_URL}/api/customer-portal/vehicles`,
        { headers }
      );

      setVehicles(vehiclesRes.data.vehicles || []);

      // Use customer data from context for basic info
      if (customerData?.customer) {
        const customerInfo = customerData.customer as any;
        form.setFieldsValue({
          first_name: customerInfo.first_name,
          last_name: customerInfo.last_name,
          email: customerInfo.email,
          phone: customerInfo.phone,
          birthday: customerInfo.birthday ? dayjs(customerInfo.birthday) : undefined,
          address: customerInfo.address,
          city: customerInfo.city,
          state: customerInfo.state,
          zip: customerInfo.zip,
        });
      }
    } catch (error) {
      console.error('Error loading profile data:', error);
      message.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  }, [accessToken, customerData, form]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/customer/login');
      return;
    }
    loadProfileData();
  }, [isAuthenticated, navigate, loadProfileData]);

  const handleSaveProfile = async (values: ProfileFormValues) => {
    setSaving(true);
    try {
      const updateData = {
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email,
        phone: values.phone,
        birthday: values.birthday ? values.birthday.format('YYYY-MM-DD') : undefined,
        address: values.address,
        city: values.city,
        state: values.state,
        zip: values.zip,
      };

      await axios.put(
        `${API_URL}/api/customer-portal/profile`,
        updateData,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      message.success('Profile updated successfully!');
      loadProfileData();
    } catch (error: any) {
      console.error('Error saving profile:', error);
      message.error(error.response?.data?.error || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const vehicleColumns = [
    {
      title: 'Vehicle',
      dataIndex: 'vehicle',
      key: 'vehicle',
      render: (text: string, record: Vehicle) => (
        <Text>{record.year} {record.make} {record.model}</Text>
      ),
    },
    {
      title: 'License Plate',
      dataIndex: 'license_plate',
      key: 'license_plate',
    },
    {
      title: 'Mileage',
      dataIndex: 'mileage',
      key: 'mileage',
      render: (mileage: number) => (
        <Text>{mileage.toLocaleString()} mi</Text>
      ),
    },
    {
      title: 'VIN',
      dataIndex: 'vin',
      key: 'vin',
      render: (vin: string) => (
        <Text code>{vin.slice(-8)}</Text>
      ),
    },
  ];

  if (loading) {
    return (
      <PublicLayout>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <Spin size="large" />
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(180deg, #f0f2f5 0%, #ffffff 100%)',
          padding: '40px 20px',
        }}
      >
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          {/* Header */}
          <Card style={{ marginBottom: 24 }}>
            <Space direction="vertical" size={0}>
              <Title level={3} style={{ margin: 0 }}>
                <UserOutlined /> My Profile
              </Title>
              <Text type="secondary">
                Manage your personal information and settings
              </Text>
            </Space>
          </Card>

          {/* Personal Information Section */}
          <Card title={<Text strong>Personal Information</Text>} style={{ marginBottom: 24 }}>
            <Form<ProfileFormValues>
              form={form}
              layout="vertical"
              onFinish={handleSaveProfile}
            >
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="first_name"
                    label="First Name"
                    rules={[{ required: true, message: 'Please enter first name' }]}
                  >
                    <Input prefix={<UserOutlined />} placeholder="First Name" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="last_name"
                    label="Last Name"
                    rules={[{ required: true, message: 'Please enter last name' }]}
                  >
                    <Input prefix={<UserOutlined />} placeholder="Last Name" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="email"
                    label="Email Address"
                    rules={[
                      { required: true, message: 'Please enter email' },
                      { type: 'email', message: 'Invalid email format' },
                    ]}
                  >
                    <Input prefix={<MailOutlined />} placeholder="Email Address" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="phone"
                    label="Phone Number"
                    rules={[{ required: true, message: 'Please enter phone' }]}
                  >
                    <Input prefix={<PhoneOutlined />} placeholder="Phone Number" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="birthday"
                label="Birthday"
              >
                <DatePicker style={{ width: '100%' }} placeholder="Select your birthday" />
              </Form.Item>

              <Form.Item
                name="address"
                label="Street Address"
              >
                <Input prefix={<HomeOutlined />} placeholder="Street Address" />
              </Form.Item>

              <Row gutter={16}>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item name="city" label="City">
                    <Input placeholder="City" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item name="state" label="State">
                    <Input placeholder="State" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item name="zip" label="ZIP Code">
                    <Input placeholder="ZIP Code" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col>
                  <Button
                    type="primary"
                    size="large"
                    htmlType="submit"
                    loading={saving}
                  >
                    Save Changes
                  </Button>
                </Col>
                <Col>
                  <Button
                    size="large"
                    onClick={() => loadProfileData()}
                  >
                    Cancel
                  </Button>
                </Col>
              </Row>
            </Form>
          </Card>

          {/* Vehicles Section */}
          <Card
            title={
              <Space>
                <CarOutlined />
                <Text strong>Linked Vehicles</Text>
              </Space>
            }
            extra={
              <Button type="link" onClick={() => navigate('/customer/vehicles')}>
                View All →
              </Button>
            }
            style={{ marginBottom: 24 }}
          >
            {vehicles.length > 0 ? (
              <Table
                columns={vehicleColumns}
                dataSource={vehicles.slice(0, 3).map((v) => ({
                  ...v,
                  key: v.id,
                }))}
                pagination={false}
                size="small"
              />
            ) : (
              <Empty
                description="No vehicles linked"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </Card>

          {/* Security Section */}
          <Card title={<Text strong>Security</Text>} style={{ marginBottom: 24 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Paragraph type="secondary">
                <LockOutlined /> Keep your account secure by regularly updating your password
              </Paragraph>
              <Button
                onClick={() => setShowPasswordModal(true)}
                disabled
              >
                Change Password (Coming Soon)
              </Button>
            </Space>
          </Card>

          {/* Info Card */}
          <Card style={{ background: '#f0f2f5' }}>
            <Space direction="vertical" size="small">
              <Text strong>Your Information</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                We keep your personal information secure and confidential. You can update
                any of these details at any time to ensure your account information is current.
              </Text>
            </Space>
          </Card>
        </div>
      </div>
    </PublicLayout>
  );
};

export default CustomerProfile;
